import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  Heart, 
  Bookmark, 
  MapPin, 
  Clock, 
  MessageSquare, 
  Share2,
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  LucideIcon,
  MoreVertical,
  Trash2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { updateIncidentPresence, deleteIncident } from '../services/incidents';
import { logger } from '../lib/logger';
import type { Incident } from '../types';
import { toast } from './ui/use-toast';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu';

interface IncidentCardProps {
  incident: Incident;
  onViewDetails?: () => void;
  isProfileView?: boolean;
  onDelete?: (incidentId: string) => void;
}

export default function IncidentCard({ 
  incident, 
  onViewDetails, 
  isProfileView = false,
  onDelete 
}: IncidentCardProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);
  const { t } = useTranslation();
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // For auto-play on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.7 } // Card needs to be 70% visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  // Handle video autoplay
  useEffect(() => {
    if (
      isVisible && 
      videoRef.current && 
      incident.incident_media && 
      incident.incident_media.length > 0 && 
      incident.incident_media[0].type === 'video'
    ) {
      // Auto play when in view
      videoRef.current.play()
        .then(() => setIsPlayingVideo(true))
        .catch(() => setIsPlayingVideo(false));
    } else if (!isVisible && videoRef.current) {
      videoRef.current.pause();
      setIsPlayingVideo(false);
    }
  }, [isVisible, incident]);

  const handlePresenceUpdate = async (type: 'still-here' | 'no-longer-here', event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (isUpdating) return;

    try {
      logger.info('Updating incident presence', 'incident_presence', { id: incident.id, type });
      setIsUpdating(true);
      await updateIncidentPresence(incident.id, type);
      await queryClient.invalidateQueries({ queryKey: ['incidents'] });
      
      if (type === 'still-here') {
        setLiked(true);
      } else {
        setSaved(true);
      }
    } catch (error) {
      logger.error(error, 'update_presence');
    } finally {
      setIsUpdating(false);
    }
  };
  
  const handleCardClick = () => {
    if (onViewDetails) {
      onViewDetails();
    }
    navigate(`/incident/${incident.slug}`);
  };

  // Get severity color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return '#FF375F';
      case 'HIGH': return '#FF7A00';
      case 'MODERATE': return '#FFBD00';
      case 'LOW': return '#32D74B';
      default: return '#FF7A00';
    }
  };

  // Get incident icon
  const getIncidentIcon = (type: string) => {
    switch (type) {
      case 'GANG_ACTIVITY': return '👥';
      case 'SEXUAL_VIOLENCE': return '⚠️';
      case 'CIVIL_UNREST': return '🔥';
      case 'KIDNAPPING': return '🚨';
      case 'ROBBERY': return '💰';
      case 'NATURAL_DISASTER': return '🌪️';
      case 'ROAD_CLOSURE': return '🚧';
      default: return '⚠️';
    }
  };
  
  // Choose the media to display (prefer video, then image, then fallback to icon)
  const getPreferredMedia = () => {
    if (!incident.incident_media || incident.incident_media.length === 0) {
      return null;
    }
    
    // First try to find a video
    const videoMedia = incident.incident_media.find(media => media.type === 'video');
    if (videoMedia) return videoMedia;
    
    // If no video, use the first image
    const imageMedia = incident.incident_media.find(media => media.type === 'image');
    if (imageMedia) return imageMedia;
    
    return null;
  };
  
  const mediaToShow = getPreferredMedia();
  const hasMedia = !!mediaToShow;
  
  // Format relative time
  const getRelativeTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) return `${diffMins}m`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  };

  // Add this function to handle incident deletion
  const handleDeleteIncident = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      const success = await deleteIncident(incident.id);
      
      if (success) {
        toast({
          title: t('incident.delete.success'),
          description: t('incident.delete.successDescription'),
        });
        
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['userIncidents'] });
        
        // Call the onDelete callback if provided
        if (onDelete) {
          onDelete(incident.id);
        }
      } else {
        toast({
          title: t('incident.delete.error'),
          description: t('incident.delete.errorDescription'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      logger.error(error, 'handleDeleteIncident');
      toast({
        title: t('incident.delete.error'),
        description: t('incident.delete.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div 
      ref={cardRef}
      className="relative bg-white rounded-lg shadow-md overflow-hidden cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Add the 3-dot menu for profile view */}
      {isProfileView && (
        <div className="absolute top-4 right-16 z-20">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="bg-black bg-opacity-60 rounded-full p-1.5 text-white hover:bg-opacity-80 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                onClick={handleDeleteIncident}
                disabled={isDeleting}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {isDeleting ? t('incident.delete.deleting') : t('incident.delete.action')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Main clickable area */}
      <div 
        className="w-full h-full relative cursor-pointer overflow-hidden"
        onClick={handleCardClick}
      >
        {/* Media container */}
        <div className="relative w-full h-[440px] bg-gray-900 overflow-hidden">
          {hasMedia ? (
            mediaToShow.type === 'video' ? (
              <video
                ref={videoRef}
                src={mediaToShow.url}
                className="w-full h-full object-cover"
                loop
                muted
                playsInline
                poster="/img/video-poster.jpg"
              />
            ) : (
              <img
                src={mediaToShow.url}
                alt={t(`incident.types.${incident.type}`)}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )
          ) : (
            // Fallback when no media is available
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
              <span className="text-7xl">{getIncidentIcon(incident.type)}</span>
            </div>
          )}
          
          {/* Incident type badge */}
          <div className="absolute top-4 left-4 z-10">
            <div className="bg-black bg-opacity-60 px-3 py-1 rounded-full flex items-center">
              <span className="mr-1">{getIncidentIcon(incident.type)}</span>
              <span className="text-white font-medium text-sm">{t(`incident.types.${incident.type}`)}</span>
            </div>
          </div>
          
          {/* Severity badge */}
          <div className="absolute top-4 right-4 z-10">
            <div 
              className="rounded-full px-2 py-1 text-xs font-medium text-white" 
              style={{ backgroundColor: getSeverityColor(incident.severity) }}
            >
              {t(`incident.severity.${incident.severity}`)}
            </div>
          </div>
          
          {/* Verification badge */}
          {incident.still_here_count > incident.no_longer_here_count && (
            <div className="absolute top-16 left-4 z-10 bg-blue-500 px-2 py-1 rounded-full flex items-center">
              <AlertTriangle size={12} className="text-white mr-1" />
              <span className="text-white text-xs font-medium">Verified</span>
            </div>
          )}
          
          {/* Video indicator */}
          {mediaToShow?.type === 'video' && (
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-60 rounded px-2 py-1 text-xs">
              <span>{isPlayingVideo ? '▶ Playing' : 'Video'}</span>
            </div>
          )}
          
          {/* Dark gradient overlay for text visibility */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent"></div>
        </div>
        
        {/* Info overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-sm font-medium text-white leading-tight line-clamp-2 mb-2">
            {incident.description || t(`incident.types.${incident.type}`)}
          </p>
          
          <div className="flex items-center text-xs text-gray-300">
            <div className="flex items-center mr-3">
              <Clock size={12} className="mr-1" />
              <span>{getRelativeTime(incident.created_at)}</span>
            </div>
            <div className="flex items-center">
              <MapPin size={12} className="mr-1" />
              <span className="truncate max-w-32">{incident.location_zone}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="flex items-center justify-between bg-gray-900 px-6 py-3 border-t border-gray-800">
        <button 
          className="flex flex-col items-center" 
          onClick={(e) => {
            e.stopPropagation();
            setLiked(!liked);
            if (!liked) handlePresenceUpdate('still-here');
          }}
        >
          <Heart 
            size={22} 
            className={liked ? "text-pink-500" : "text-white"} 
            fill={liked ? "#ec4899" : "none"}
          />
          <span className="text-xs mt-1">{incident.still_here_count}</span>
        </button>
        
        <button 
          className="flex flex-col items-center"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/incident/${incident.slug}`);
          }}
        >
          <MessageSquare size={22} />
          <span className="text-xs mt-1">{incident.comments?.length || 0}</span>
        </button>
        
        <button 
          className="flex flex-col items-center"
          onClick={(e) => {
            e.stopPropagation();
            // Simple share function
            if (navigator.share) {
              navigator.share({
                title: t(`incident.types.${incident.type}`),
                text: incident.description || t(`incident.types.${incident.type}`),
                url: `${window.location.origin}/incident/${incident.slug}`,
              }).catch((error) => console.log('Error sharing', error));
            } else {
              toast({
                title: "Sharing not supported",
                description: "Your browser doesn't support sharing.",
              });
            }
          }}
        >
          <Share2 size={22} />
          <span className="text-xs mt-1">Share</span>
        </button>
        
        <button 
          className="flex flex-col items-center"
          onClick={(e) => {
            e.stopPropagation();
            setSaved(!saved);
            if (!saved) handlePresenceUpdate('no-longer-here');
          }}
        >
          <Bookmark 
            size={22} 
            className={saved ? "text-yellow-500" : "text-white"} 
            fill={saved ? "#eab308" : "none"}
          />
          <span className="text-xs mt-1">{incident.no_longer_here_count}</span>
        </button>
      </div>
    </div>
  );
}